/**
 * Forge 3.1 · Project Universe · Page
 *
 * PC/Tablet (>=768px): Immersive 3D universe with physics + pro camera
 * Mobile (<768px):     Compact list view, module cards, no 3D
 *
 * The "wow" landing for Forge — every file/broker/tool is a real node.
 */
import React, { Suspense, useEffect, useMemo, useState } from "react";
import { useUniverseGraph } from "./useUniverseGraph";
import { usePendingProposals } from "../memory/memoryApi";
import "./universe.css";

const UniverseScene = React.lazy(() => import("./UniverseScene.jsx"));

const SCOPES = [
  { id: "all",     label: "Tutto il codebase", hint: "Vista completa dell'organismo" },
  { id: "hub",     label: "Hub",              hint: "Aria Hub AI + Identity + Tracking" },
  { id: "aria",    label: "Aria",             hint: "Chat, runtime, orchestrator" },
  { id: "kernel",  label: "Kernel",           hint: "Router, execution, capabilities" },
  { id: "ads",     label: "TotalKlickADS",    hint: "Advertising engine" },
  { id: "billing", label: "Billing",          hint: "Stripe + fatturazione" },
];

// ══════════════════════════════════════════════════════════
// Global navigation bar — always visible on Universe/Memory
// so the user never loses the original Forge Console / Aria chat
// ══════════════════════════════════════════════════════════
const NAV_LINKS = [
  { href: "/forge",              label: "Workspace",      hint: "Home cognitiva · Aria è già qui" },
  { href: "/forge/aria",         label: "Cockpit Aria",   hint: "Autonomy OS · score, proposte, action bus" },
  { href: "/api/forge-studio/",  label: "Forge Console",  hint: "Console originale · CoreSphere · Radial Menu" },
  { href: "/universe",           label: "Universe",       hint: "Organismo 3D del codebase" },
  { href: "/memory",             label: "Memory",         hint: "Memoria persistente Forge" },
  { href: "/providers",          label: "AI Providers",   hint: "Gestione chiavi e provider LLM" },
  { href: "/bench",              label: "AI Bench",       hint: "Benchmark provider · latenza/costo/errori" },
  { href: "/",                   label: "Home Hub",       hint: "Homepage marketing / Hub AI" },
];

export function ForgeNavBar({ current }) {
  return (
    <nav className="forge-navbar" data-testid="forge-navbar">
      <div className="forge-navbar-brand">FORGE</div>
      {NAV_LINKS.map((l) => (
        <a
          key={l.href}
          href={l.href}
          className={"forge-navbar-link " + (current === l.href ? "active" : "")}
          title={l.hint}
          data-testid={`forge-nav-${l.label.toLowerCase().replace(/\s+/g,"-")}`}
        >
          {l.label}
        </a>
      ))}
    </nav>
  );
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isMobile;
}

function useIsTouch() {
  return typeof window !== "undefined" && "ontouchstart" in window;
}

// ══════════════════════════════════════════════════════════
// MOBILE VIEW · lista compatta, no 3D
// ══════════════════════════════════════════════════════════
function MobileUniverse() {
  const [scope, setScope] = useState("all");
  const { loading, error, nodes, modules, stats } = useUniverseGraph({
    scope,
    maxNodes: 300,
  });

  const grouped = useMemo(() => {
    const g = {};
    nodes.forEach((n) => {
      if (n.type === "module") return;
      const key = n.module || "misc";
      if (!g[key]) g[key] = [];
      g[key].push(n);
    });
    return g;
  }, [nodes]);

  return (
    <div className="uni-mobile" data-testid="universe-mobile">
      <ForgeNavBar current="/universe" />
      <header className="uni-mobile-head">
        <div>
          <div className="uni-mobile-title">FORGE UNIVERSE</div>
          <div className="uni-mobile-sub">
            {stats?.node_count ?? "…"} nodi · {modules.length} moduli
          </div>
        </div>
      </header>
      <div className="uni-mobile-scopes">
        {SCOPES.map((s) => (
          <button
            key={s.id}
            className={"uni-chip " + (scope === s.id ? "active" : "")}
            onClick={() => setScope(s.id)}
            data-testid={`uni-mobile-scope-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>
      {loading && <div className="uni-mobile-loading">Carico l&apos;universo…</div>}
      {error && <div className="uni-mobile-error">Errore: {error}</div>}
      {!loading && !error && (
        <div className="uni-mobile-list">
          {Object.entries(grouped).map(([mod, items]) => (
            <section key={mod} className="uni-mobile-section">
              <h3>
                <span
                  className="uni-mobile-dot"
                  style={{ background: items[0]?.color || "#8b5cf6" }}
                />
                {mod.toUpperCase()}
                <em>{items.length}</em>
              </h3>
              <ul>
                {items.slice(0, 15).map((n) => (
                  <li key={n.id}>
                    <span className="uni-mobile-type">{n.type}</span>
                    <span className="uni-mobile-name">{n.label}</span>
                  </li>
                ))}
                {items.length > 15 && (
                  <li className="uni-mobile-more">… e altri {items.length - 15}</li>
                )}
              </ul>
            </section>
          ))}
        </div>
      )}
      <div className="uni-mobile-footer">
        Vista 3D disponibile su tablet e PC · pinch-zoom + orbit
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// DESKTOP/TABLET VIEW · 3D immersivo
// ══════════════════════════════════════════════════════════
function DesktopUniverse() {
  const [scope, setScope] = useState("all");
  const [maxNodes, setMaxNodes] = useState(500);
  const [selected, setSelected] = useState(null);
  const [focusTarget, setFocusTarget] = useState(null);
  const [fps, setFps] = useState(60);
  const [showTutorial, setShowTutorial] = useState(() => {
    if (typeof window === "undefined") return true;
    return !localStorage.getItem("forge:universe:seen-tutorial");
  });
  const isTouch = useIsTouch();
  const { loading, error, nodes, edges, stats, modules } = useUniverseGraph({
    scope,
    maxNodes,
  });

  const dismissTutorial = () => {
    setShowTutorial(false);
    try { localStorage.setItem("forge:universe:seen-tutorial", "1"); } catch (e) { /* ignore quota errors */ }
  };

  const handleSelectNode = (n) => {
    setSelected(n);
    setFocusTarget(n);
  };

  const jumpToModule = (modId) => {
    const modNode = nodes.find((n) => n.id === `mod:${modId}`);
    if (modNode) {
      setSelected(modNode);
      setFocusTarget(modNode);
    }
  };

  return (
    <div className="uni-root" data-testid="universe-desktop">
      <ForgeNavBar current="/universe" />
      {/* 3D Canvas layer */}
      <div className="uni-canvas-layer">
        <Suspense fallback={<div className="uni-loading">Costruisco l&apos;universo…</div>}>
          {!loading && !error && nodes.length > 0 && (
            <UniverseScene
              nodes={nodes}
              edges={edges}
              onSelectNode={handleSelectNode}
              onFps={setFps}
              focusTarget={focusTarget}
              isTouch={isTouch}
            />
          )}
        </Suspense>
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="uni-loading-overlay" data-testid="uni-loading">
          <div className="uni-loading-orb" />
          <div className="uni-loading-text">Costruisco il tuo universo…</div>
        </div>
      )}

      {error && (
        <div className="uni-error-overlay">
          <div>Non riesco a caricare il grafo</div>
          <div className="uni-error-detail">{error}</div>
        </div>
      )}

      {/* Top-left: brand + stats HUD */}
      <div className="uni-hud uni-hud-tl" data-testid="uni-hud">
        <div className="uni-brand">
          <span className="uni-brand-mark" />
          <span>FORGE &middot; UNIVERSE</span>
          <span className="uni-brand-ver">3.1</span>
        </div>
        {stats && (
          <div className="uni-stats">
            <span><b>{stats.node_count}</b> nodi</span>
            <span><b>{stats.edge_count}</b> connessioni</span>
            <span className={"uni-fps " + (fps < 40 ? "low" : "")}>{fps} fps</span>
          </div>
        )}
        <MemoryLink />
      </div>

      {/* Top-right: scope filters */}
      <div className="uni-hud uni-hud-tr">
        <div className="uni-scope-title">Vista</div>
        <div className="uni-scope-list">
          {SCOPES.map((s) => (
            <button
              key={s.id}
              className={"uni-scope " + (scope === s.id ? "active" : "")}
              onClick={() => { setScope(s.id); setSelected(null); }}
              title={s.hint}
              data-testid={`uni-scope-${s.id}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Bottom-left: module planets nav */}
      {modules.length > 0 && (
        <div className="uni-hud uni-hud-bl">
          <div className="uni-scope-title">Pianeti</div>
          <div className="uni-planet-list">
            {modules.slice(0, 8).map((m) => (
              <button
                key={m.id}
                className="uni-planet"
                onClick={() => jumpToModule(m.id)}
                data-testid={`uni-planet-${m.id}`}
              >
                <span className="uni-planet-name">{m.label}</span>
                <span className="uni-planet-count">{m.count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Bottom-right: selected node panel */}
      {selected && (
        <div className="uni-hud uni-hud-br uni-panel" data-testid="uni-node-panel">
          <div className="uni-panel-head">
            <span
              className="uni-panel-dot"
              style={{ background: selected.color }}
            />
            <div>
              <div className="uni-panel-title">{selected.label}</div>
              <div className="uni-panel-sub">
                {selected.type} · {selected.module}
              </div>
            </div>
            <button
              className="uni-panel-close"
              onClick={() => { setSelected(null); setFocusTarget(null); }}
              data-testid="uni-panel-close"
              aria-label="Chiudi"
            >×</button>
          </div>
          {selected.meta && Object.keys(selected.meta).length > 0 && (
            <div className="uni-panel-meta">
              {Object.entries(selected.meta).slice(0, 8).map(([k, v]) => (
                <div key={k} className="uni-panel-meta-row">
                  <span>{k.replaceAll("_", " ")}</span>
                  <b>
                    {Array.isArray(v)
                      ? `${v.length} elementi`
                      : typeof v === "object"
                      ? JSON.stringify(v).slice(0, 60)
                      : String(v).slice(0, 60)}
                  </b>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tutorial overlay (first time) */}
      {showTutorial && !loading && !error && (
        <div className="uni-tutorial-overlay" onClick={dismissTutorial}>
          <div className="uni-tutorial-card" onClick={(e) => e.stopPropagation()}>
            <h2>Benvenuto nel Forge Universe</h2>
            <p>
              Ogni sfera è un pezzo reale del tuo software. I <b>pianeti</b> più
              grandi sono i moduli. Le sfere piccole sono file, endpoint, broker
              e strumenti. Le linee sono le connessioni reali (import, uses,
              expose).
            </p>
            <div className="uni-tutorial-controls">
              <div><b>Trascina</b> per orbitare</div>
              <div><b>Rotellina / pinch</b> per zoom</div>
              <div><b>Tasto destro / due dita</b> per spostare</div>
              <div><b>Clic sferà</b> per zoomare su di essa</div>
            </div>
            <button
              className="uni-tutorial-btn"
              onClick={dismissTutorial}
              data-testid="uni-tutorial-dismiss"
            >
              Entra nell&apos;universo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Universe() {
  const isMobile = useIsMobile();

  // Prevent iOS/trackpad pinch zoom of the whole page (which would zoom
  // Chrome's viewport, not the 3D environment). Bare/ctrl wheel is left
  // alone: OrbitControls handles zoom internally with any wheel event.
  useEffect(() => {
    const onGesture = (e) => e.preventDefault();
    document.addEventListener("gesturestart", onGesture);
    document.addEventListener("gesturechange", onGesture);
    return () => {
      document.removeEventListener("gesturestart", onGesture);
      document.removeEventListener("gesturechange", onGesture);
    };
  }, []);

  return isMobile ? <MobileUniverse /> : <DesktopUniverse />;
}

// ══════════════════════════════════════════════════════════
// Memory link · shows pending-proposals badge
// ══════════════════════════════════════════════════════════
function MemoryLink() {
  const { proposals } = usePendingProposals(15000);
  const count = proposals.length;
  return (
    <a
      href="/memory"
      className="uni-memory-link"
      title="Forge Memory Engine"
      data-testid="uni-memory-link"
    >
      <span className="uni-memory-icon">◈</span>
      Memoria
      {count > 0 && (
        <span className="uni-memory-badge" data-testid="uni-memory-badge">
          {count}
        </span>
      )}
    </a>
  );
}
